import * as React from 'react';

// MUI
import { grey } from '@mui/material/colors';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';

// Third Party
import { useQuery, useMutation } from '@apollo/client';
import { Link as RouterLink } from "react-router-dom";

// Internal
import { GET_ADMIN_PUBLICATIONS } from '../../data/graphql/queries';
import { DECIDE_PUBLICATION, UNAPPROVE_PUBLICATION } from '../../data/graphql/mutations';
import { PublicationType } from '../../data/types';
import en from '../../data/en.json';

// Components
import PublicationCard from '../../components/PublicationCard';
import EditPublication from './components/EditPublication';

interface PublicationsTabType {
  index: number
  label: string
}

export default function AdminPublications() {
  const { loading, error, data, refetch } = useQuery(GET_ADMIN_PUBLICATIONS, {
    variables: { paging_offset: 0, paging_limit: 100 },
  });

  const [decidePublication, { loading: deciding }] = useMutation(DECIDE_PUBLICATION);
  const [unapprovePublication, { loading: unapproving }] = useMutation(UNAPPROVE_PUBLICATION);

  const publiationTabTypes: PublicationsTabType[] = [
    { index: 0, label: en.adminDashboard.publicationsSection.pending },
    { index: 1, label: en.adminDashboard.publicationsSection.approved },
  ]

  const [currentTab, setCurrentTab] = React.useState<PublicationsTabType>(publiationTabTypes[0]);
  const [editPublication, setEditPublication] = React.useState<PublicationType | null>(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [approvingPublication, setApprovingPublication] = React.useState<PublicationType | null>(null);
  const [approvalNote, setApprovalNote] = React.useState('');
  const [unapprovingPublication, setUnapprovingPublication] = React.useState<PublicationType | null>(null);
  const [snackbarMessage, setSnackbarMessage] = React.useState<string | null>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(publiationTabTypes.find(tabType => tabType.index === newValue) || currentTab)
  };

  const isPendingTab = currentTab.label === en.adminDashboard.publicationsSection.pending;

  const handleConfirmApprove = async () => {
    if (!approvingPublication) return;
    try {
      await decidePublication({
        variables: {
          uuid: approvingPublication.uuid,
          decision: 'approved',
          details: approvalNote.trim(),
        },
      });
      setApprovingPublication(null);
      setApprovalNote('');
      setSnackbarMessage(en.adminDashboard.publicationsSection.approveSuccess);
      await refetch();
    } catch (e) {
      setSnackbarMessage(en.adminDashboard.publicationsSection.approveFailure);
    }
  };

  const handleConfirmUnapprove = async () => {
    if (!unapprovingPublication) return;
    try {
      await unapprovePublication({
        variables: { uuid: unapprovingPublication.uuid },
      });
      setUnapprovingPublication(null);
      setSnackbarMessage(en.adminDashboard.publicationsSection.unapproveSuccess);
      await refetch();
    } catch (e) {
      setSnackbarMessage(en.adminDashboard.publicationsSection.unapproveFailure);
    }
  };

  if (loading) return <p>{en.common['loading..']}</p>
  if (error) return <p>{en.common.error}: {error.message}</p>

  const publications: PublicationType[] = data.adminPublications as PublicationType[]
  const selectedPublications: PublicationType[] = publications
    .filter((publication: PublicationType) => publication.status === currentTab.label)
    .filter((publication: PublicationType) =>
      searchTerm.trim() === '' ||
      publication.title?.toLowerCase().includes(searchTerm.trim().toLowerCase()) ||
      publication.user?.profile?.nickname?.toLowerCase().includes(searchTerm.trim().toLowerCase())
    )

  const renderPublications = (
    <React.Fragment>
      <Tabs value={currentTab.index} onChange={handleTabChange}>
        <Tab label={en.adminDashboard.publicationsSection.pending} />
        <Tab label={en.adminDashboard.publicationsSection.approved} />
      </Tabs>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <TextField
          size="small"
          placeholder={en.adminDashboard.publicationsSection.searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: '45vw' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Grid container flexDirection="column" alignItems="center">
        {selectedPublications.length > 0 &&
          isPendingTab &&
          selectedPublications.map((publication: PublicationType, index: number) => (
            <Box
              key={index}
              sx={{ width: "45vw", m: 2, p: 2, border: 1, borderColor: grey[300] }}
            >
              <Link
                component={"button"}
                onClick={() => setEditPublication(publication)}
                style={{ textDecoration: 'none', color: grey[900], width: '100%', textAlign: 'left' }}
              >
                <PublicationCard publication={publication} />
              </Link>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  sx={{ bgcolor: 'primary.dark', '&:hover': { bgcolor: 'primary.dark' } }}
                  onClick={() => {
                    setApprovingPublication(publication);
                    setApprovalNote('');
                  }}
                >
                  {en.adminDashboard.publicationsSection.approve}
                </Button>
              </Box>
            </Box>
          ))}

        {selectedPublications.length > 0 &&
          !isPendingTab &&
          selectedPublications.map((publication: PublicationType, index: number) => (
            <Box
              key={index}
              sx={{ width: "45vw", m: 2, p: 2, border: 1, borderColor: grey[300] }}
            >
              <RouterLink
                to={`/publications/${publication.uuid}`}
                style={{ textDecoration: 'none', color: 'black' }}
              >
                <PublicationCard publication={publication} />
              </RouterLink>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setUnapprovingPublication(publication)}
                >
                  {en.adminDashboard.publicationsSection.unapprove}
                </Button>
              </Box>
            </Box>
          ))}

        {selectedPublications.length === 0 &&
          <Typography variant='h3' color={grey[300]} sx={{ mt: 2 }}>
            {en.common.noPublication}
          </Typography>
        }
      </Grid>

      <Dialog open={Boolean(approvingPublication)} onClose={() => setApprovingPublication(null)} fullWidth maxWidth="sm">
        <DialogTitle>{en.adminDashboard.publicationsSection.approvePublication}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color={grey[700]} sx={{ mb: 2 }}>
            {approvingPublication?.title}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label={en.adminDashboard.publicationsSection.approveNoteLabel}
            value={approvalNote}
            onChange={(e) => setApprovalNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovingPublication(null)} disabled={deciding}>
            {en.common.cancel}
          </Button>
          <Button
            variant="contained"
            sx={{ bgcolor: 'primary.dark', '&:hover': { bgcolor: 'primary.dark' } }}
            onClick={handleConfirmApprove}
            disabled={deciding}
          >
            {deciding ? en.common['loading..'] : en.adminDashboard.publicationsSection.approve}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(unapprovingPublication)} onClose={() => setUnapprovingPublication(null)} fullWidth maxWidth="sm">
        <DialogTitle>{en.adminDashboard.publicationsSection.unapprovePublication}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color={grey[700]} sx={{ mb: 2 }}>
            {unapprovingPublication?.title}
          </Typography>
          <Typography variant="body2" color={grey[700]}>
            {en.adminDashboard.publicationsSection.unapproveConfirm}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnapprovingPublication(null)} disabled={unapproving}>
            {en.common.cancel}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmUnapprove}
            disabled={unapproving}
          >
            {unapproving ? en.common['loading..'] : en.adminDashboard.publicationsSection.unapprove}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarMessage !== null}
        autoHideDuration={3000}
        message={snackbarMessage}
        onClose={() => setSnackbarMessage(null)}
      />
    </React.Fragment>
  )
  if (editPublication) {
    return (
      <EditPublication
        publication={editPublication}
        setEditPublication={setEditPublication}
      />
    )
  }

  return renderPublications;
}
